/** Reimport tab — resubmit this run's input as a NEW import run (the
 *  dashboard's Reimport tab, console edition).
 *
 *  Options: send notifications (defaults to what the original run used) and
 *  "Edit XML" — a monospace editor prefilled with the stored input; when off,
 *  the server reuses the stored input untouched. Success navigates to the new
 *  run's dialog.
 */
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as importRunsApi from '@/api/importRuns';
import type { ImportRunDetail } from '@/api/importRuns';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';

export function ReimportTab({
  detail,
  detailPath,
}: {
  detail: ImportRunDetail;
  detailPath: (id: string) => string;
}) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sendNotifications, setSendNotifications] = useState(
    Boolean(detail.input_options?.send_notifications),
  );
  const [editXml, setEditXml] = useState(false);
  const [xmlText, setXmlText] = useState<string | null>(null);

  const input = useQuery({
    queryKey: ['import-runs', 'input', detail.id],
    queryFn: () => importRunsApi.inputData(detail.id),
    staleTime: Infinity,
    enabled: editXml,
  });

  const storedText = input.data?.input_data ?? '';
  const effectiveText = xmlText ?? storedText;

  const mutation = useMutation({
    mutationFn: () =>
      importRunsApi.resubmit(detail.id, {
        send_notifications: sendNotifications,
        data: editXml && xmlText !== null ? xmlText : null,
      }),
    onSuccess: (res) => {
      snackbar.show(`Submitted for another import as run ${res.id.slice(0, 8)}…`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['import-runs'] });
      navigate(detailPath(res.id));
    },
    onError: (e) => {
      const message = e instanceof ConsoleApiError ? e.message : 'Reimport failed.';
      snackbar.show(`Reimport failed: ${message}`, 'error');
    },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, flex: 1 }}>
      <Alert severity="warning">
        Reimporting creates a NEW import run (origin Console, owned by this run&apos;s tenant) and
        re-enters the normal import pipeline immediately.
      </Alert>

      <Stack direction="row" spacing={3} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={sendNotifications}
              onChange={(e) => setSendNotifications(e.target.checked)}
            />
          }
          label="Send notifications for changes made by the reimport"
        />
        <FormControlLabel
          control={<Switch checked={editXml} onChange={(e) => setEditXml(e.target.checked)} />}
          label="Edit XML before reimporting"
        />
      </Stack>

      {editXml &&
        (input.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : input.error ? (
          <Alert severity="error">Couldn’t load the stored input for editing.</Alert>
        ) : (
          <TextField
            multiline
            minRows={14}
            maxRows={24}
            value={effectiveText}
            onChange={(e) => setXmlText(e.target.value)}
            slotProps={{
              input: { sx: { fontFamily: 'monospace', fontSize: 12.5 } },
            }}
          />
        ))}

      <Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ReplayIcon />}
          disabled={mutation.isPending || (editXml && (input.isLoading || Boolean(input.error)))}
          onClick={() => mutation.mutate()}
        >
          Reimport now
        </Button>
      </Box>
    </Box>
  );
}
